/**
 * Folha de orientação para liberar a localização no app do prestador.
 * Explica o motivo antes do pedido do aparelho e, se bloqueada, mostra
 * os passos de liberação conforme o aparelho — nunca como "erro".
 *
 * RELEASE: 2026-10-21 v1.0.0 - Versão inicial.
 */
import { useMemo } from 'react';
import { Loader2, LocateFixed, MapPinOff, RefreshCw, SignalLow } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { detectPlatform, permissionSteps, type GeoFailure } from '@/lib/geoPermission';

export type LocationSheetState = 'intro' | 'locating' | GeoFailure;

interface Props {
  open: boolean;
  state: LocationSheetState;
  /** Motivo exibido na introdução (ex.: registrar o ponto de saída). */
  reason: string;
  onRequest: () => void;
  onClose: () => void;
}

export function LocationPermissionSheet({ open, state, reason, onRequest, onClose }: Props) {
  const steps = useMemo(() => permissionSteps(detectPlatform(typeof navigator !== 'undefined' ? navigator.userAgent : '')), []);
  const blocked = state === 'denied';
  const weak = state === 'unavailable' || state === 'timeout';
  const Icon = blocked ? MapPinOff : weak ? SignalLow : LocateFixed;
  const title = blocked ? 'Localização bloqueada para este site'
    : weak ? 'Não conseguimos obter sua posição'
      : 'Permitir localização';
  const description = blocked ? 'Para continuar, libere o acesso à localização seguindo os passos abaixo.'
    : weak ? 'Vá para um local aberto, confira se a localização do aparelho está ligada e tente novamente.'
      : reason;

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent side="bottom" className="rounded-t-2xl pb-[calc(1.25rem+env(safe-area-inset-bottom))]">
        <SheetHeader className="items-center text-center">
          <span className="mb-2 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
            <Icon className="h-7 w-7 text-primary" />
          </span>
          <SheetTitle>{title}</SheetTitle>
          <SheetDescription>{description}</SheetDescription>
        </SheetHeader>
        {blocked && (
          <ol className="mt-4 space-y-2 text-sm">
            {steps.map((s, i) => (
              <li key={i} className="flex gap-3 rounded-lg bg-muted/50 p-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">{i + 1}</span>
                <span>{s}</span>
              </li>
            ))}
          </ol>
        )}
        <Button className="mt-5 h-12 w-full" disabled={state === 'locating'} onClick={onRequest}>
          {state === 'locating' ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Buscando sua localização…</>
            : state === 'intro' ? <><LocateFixed className="mr-2 h-4 w-4" />Permitir localização</>
              : <><RefreshCw className="mr-2 h-4 w-4" />Tentar novamente</>}
        </Button>
        <Button variant="ghost" className="mt-2 h-10 w-full" onClick={onClose}>Agora não</Button>
      </SheetContent>
    </Sheet>
  );
}
